// src/components/TotalWorkingDaysReport.js
import React from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

const TotalWorkingDaysReport = () => {
  const academicCalendarData = useSelector((state) => state.academicCalendar.academicData);
  const sessions = useSelector(state => state.academicCalendar.sessions);
  console.log("sessions", sessions);


  const calculateWorkingDaysDetails = (startDate, endDate, allHolidays) => {
    if (!startDate || !endDate || startDate.isAfter(endDate)) {
      return {
        totalDays: 0,
        weekendsCount: 0,
        holidaysCount: 0,
        workingDays: 0,
      };
    }

    let totalDays = 0;
    let weekendsCount = 0;
    let current = startDate.clone();
    let holidaysInPeriodCount = 0;

    while (current.isSameOrBefore(endDate, 'day')) {
      totalDays++;
      const dayOfWeek = current.day();
      if (dayOfWeek === 0 || dayOfWeek === 6) { // 0 for Sunday, 6 for Saturday
        weekendsCount++;
      }

      const isHoliday = allHolidays.some(
        (holiday) => moment(holiday.date, "DD-MM-YYYY").isSame(current, 'day')
      );
      if (isHoliday) {
        holidaysInPeriodCount++;
      }
      current.add(1, 'day');
    }

    let workingDays = totalDays - weekendsCount - holidaysInPeriodCount;
    // Account for holidays that fall on weekends (they shouldn't reduce working days twice)
    const weekendHolidays = allHolidays.filter(holiday => {
      const holidayMoment = moment(holiday.date, "DD-MM-YYYY");
      return holidayMoment.isBetween(startDate, endDate, 'day', '[]') &&
        (holidayMoment.day() === 0 || holidayMoment.day() === 6);
    });
    workingDays += weekendHolidays.length; // Add back days if a holiday was on a weekend

    return {
      totalDays,
      weekendsCount,
      holidaysCount: holidaysInPeriodCount,
      workingDays,
    };
  };


  const getReportDetails = () => {

    const activeSession = sessions?.find(s => s.status === 'active');
    let sessionStart = null;
    let sessionEnd = null;

    const sessionStartItem = academicCalendarData.find(item => item.type === 'sessionStart');
    const sessionEndItem = academicCalendarData.find(item => item.type === 'sessionEnd');

    if (sessionStartItem && sessionEndItem) {
      sessionStart = moment(sessionStartItem.date, "DD-MM-YYYY").startOf('day');
      sessionEnd = moment(sessionEndItem.date, "DD-MM-YYYY").endOf('day');
    }

    if (!sessionStart || !sessionEnd || sessionStart.isAfter(sessionEnd)) {
      return {
        totalDaysOld: "N/A",
        weekendsCountOld: "N/A",
        holidaysCountOld: "N/A",
        workingDaysOverallOld: "N/A",
        sessionStartDateFormatted: "Not Set",
        sessionEndDateFormatted: "Not Set",
        sessionDateRangeFormatted: "Session dates not set",
        termWiseDataNewCards: [],
        termWiseDataOldTable: [],
        monthWiseData: [],
        holidayList: [],
        overallTotalHolidays: "N/A",
      };
    }

    const allHolidays = academicCalendarData.filter(item => item.type === 'holiday');

    const overallDetails = calculateWorkingDaysDetails(sessionStart, sessionEnd, allHolidays);

    const monthWiseData = [];
    let currentMonth = sessionStart.clone().startOf('month');
    while (currentMonth.isSameOrBefore(sessionEnd, 'month')) {
      const monthStart = currentMonth.clone().startOf('month').isBefore(sessionStart) ? sessionStart.clone() : currentMonth.clone().startOf('month');
      const monthEnd = currentMonth.clone().endOf('month').isAfter(sessionEnd) ? sessionEnd.clone() : currentMonth.clone().endOf('month');

      if (monthStart.isSameOrBefore(monthEnd)) {
        const details = calculateWorkingDaysDetails(monthStart, monthEnd, allHolidays);
        monthWiseData.push({
          month: currentMonth.format('MMMM'),
          ...details,
        });
      }
      currentMonth.add(1, 'month');
    }

    const termWiseDataOldTable = [];

    // TERM 1
    if (activeSession?.term_one_start_date && activeSession?.term_one_end_date) {
      const term1Start = moment.utc(activeSession.term_one_start_date).local();
      const term1End = moment.utc(activeSession.term_one_end_date).local();

      const term1Details = calculateWorkingDaysDetails(
        term1Start,
        term1End,
        allHolidays
      );

      termWiseDataOldTable.push({
        term: 'Term 1',
        startDate: term1Start.format("DD MMM"),
        endDate: term1End.format("DD MMM"),
        ...term1Details,
      });
    }

    // TERM 2
    if (activeSession?.term_two_start_date && activeSession?.term_two_end_date) {
      const term2Start = moment.utc(activeSession.term_two_start_date).local();
      const term2End = moment.utc(activeSession.term_two_end_date).local();

      const term2Details = calculateWorkingDaysDetails(
        term2Start,
        term2End,
        allHolidays
      );

      termWiseDataOldTable.push({
        term: 'Term 2',
        startDate: term2Start.format("DD MMM"),
        endDate: term2End.format("DD MMM"),
        ...term2Details,
      });
    }

    const holidayList = allHolidays.filter(item =>
      moment(item.date, "DD-MM-YYYY").isBetween(sessionStart, sessionEnd, 'day', '[]')
    ).sort((a, b) => moment(a.date, "DD-MM-YYYY").valueOf() - moment(b.date, "DD-MM-YYYY").valueOf());

    const termWiseDataNewCards = termWiseDataOldTable.map(term => ({
      term: term.term,
      dateRange: `${term.startDate} - ${term.endDate}`,
      days: term.workingDays,
    }));

    const totalCalculatedWorkingDaysNew = (termWiseDataNewCards[0]?.days || 0) + (termWiseDataNewCards[1]?.days || 0);


    return {
      totalDaysOld: overallDetails.totalDays,
      weekendsCountOld: overallDetails.weekendsCount,
      holidaysCountOld: overallDetails.holidaysCount,
      workingDaysOverallOld: overallDetails.workingDays,
      sessionStartDateFormatted: sessionStart ? sessionStart.format("DD MMMM") : "N/A",
      sessionEndDateFormatted: sessionEnd ? sessionEnd.format("DD MMMM") : "N/A",

      workingDaysNew: totalCalculatedWorkingDaysNew,
      sessionDateRangeFormatted: sessionStart && sessionEnd ? `Session ${sessionStart.format("YYYY-MM-DD")} to ${sessionEnd.format("YYYY-MM-DD")}` : "Session dates not set",
      termWiseDataNewCards: termWiseDataNewCards,
      overallTotalHolidays: overallDetails.holidaysCount,

      holidayList,
      monthWiseData,
      termWiseDataOldTable: termWiseDataOldTable,
    };
  };

  const {
    workingDaysNew, sessionDateRangeFormatted, termWiseDataNewCards,
    overallTotalHolidays, sessionStartDateFormatted, sessionEndDateFormatted,
    holidayList, monthWiseData, termWiseDataOldTable
  } = getReportDetails();

  const term1NewCard = termWiseDataNewCards.find(t => t.term === 'Term 1');
  const term2NewCard = termWiseDataNewCards.find(t => t.term === 'Term 2');
  const containerStyle = {
    maxWidth: "900px",
    margin: "15px auto",
    padding: "15px 15px",
    // backgroundColor: "rgb(123 47 247 / 51%)",
    // backgroundColor: "#ffffff",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    borderRadius: "12px",
    fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
  };
  return (
    <div className="" style={containerStyle}>
      {/* First Row: Session Start, Session End, Total Working Days */}
      <div className="  mb-2 d-flex justify-content-between gap-2">
        {/* Session Start Date Card */}
        <div className="w-100">
          <div className="card p-2" style={{ backgroundColor: '#eef4ff', color: '#0056b3' }}>
            <h6 className="card-title mb-0" style={{ fontSize: '0.9em' }}>Session Start Date</h6>
            <h3 className="card-text mb-0">
              <strong>{sessionStartDateFormatted}</strong>
            </h3>
            <p className="card-text text-muted" style={{ fontSize: '0.8em' }}>
              Start of Academic Session
            </p>
          </div>
        </div>
        {/* Session End Date Card */}
        <div className="w-100">
          <div className="card p-2" style={{ backgroundColor: '#e8f9ed', color: '#28a745' }}>
            <h6 className="card-title mb-0" style={{ fontSize: '0.9em' }}>Session End Date</h6>
            <h3 className="card-text mb-0">
              <strong>{sessionEndDateFormatted}</strong>
            </h3>
            <p className="card-text text-muted" style={{ fontSize: '0.8em' }}>
              End of Academic Session
            </p>
          </div>
        </div>
        {/* Total Working Days Card (Moved to first row) */}
        <div className="w-100">
          <div className="card p-2" style={{ backgroundColor: '#f5eeff', color: '#6f42c1' }}>
            <h6 className="card-title mb-0" style={{ fontSize: '0.9em' }}>Total Working Days</h6>
            <h2 className="card-text mb-0">
              <strong>{workingDaysNew} days</strong>
            </h2>
            <p className="card-text text-muted" style={{ fontSize: '0.8em' }}>
              {sessionDateRangeFormatted}
            </p>
          </div>
        </div>
      </div>

      {/* Second Row: Term 1, Term 2, Total Holidays */}
      <div className="  mb-2 d-flex justify-content-between gap-2">
        {/* Term 1 Card */}
        <div className="w-100">
          <div className="card p-2 w-100" style={{ backgroundColor: '#eef4ff', color: '#0056b3' }}>
            <h6 className="card-title mb-0" style={{ fontSize: '0.9em' }}>Term 1</h6>
            <h3 className="card-text mb-0">
              <strong>{term1NewCard ? term1NewCard.days : 'N/A'} days</strong>
            </h3>
            <p className="card-text text-muted" style={{ fontSize: '0.8em' }}>
              {term1NewCard ? term1NewCard.dateRange : 'N/A'}
            </p>
          </div>
        </div>
        {/* Term 2 Card */}
        <div className="w-100">
          <div className="card p-2" style={{ backgroundColor: '#e8f9ed', color: '#28a745' }}>
            <h6 className="card-title mb-0" style={{ fontSize: '0.9em' }}>Term 2</h6>
            <h3 className="card-text mb-0">
              <strong>{term2NewCard ? term2NewCard.days : 'N/A'} days</strong>
            </h3>
            <p className="card-text text-muted" style={{ fontSize: '0.8em' }}>
              {term2NewCard ? term2NewCard.dateRange : 'N/A'}
            </p>
          </div>
        </div>
        {/* Total Holidays Card */}
        <div className="w-100">
          <div className="card p-2" style={{ backgroundColor: '#fff3cd', color: '#856404' }}>
            <h6 className="card-title mb-0" style={{ fontSize: '0.9em' }}>Total Holidays</h6>
            <h3 className="card-text mb-0">
              <strong>{overallTotalHolidays}</strong>
            </h3>
            <p className="card-text text-muted" style={{ fontSize: '0.8em' }}>
              Across Session
            </p>
          </div>
        </div>
      </div>

      {/* ORIGINAL UI: Month-wise Working Days table (re-enabled) */}
      {monthWiseData.length > 0 && workingDaysNew !== "N/A" && (
        <div className="card shadow mt-3" style={{ backgroundColor: '#f0f8ff', borderColor: '#b0e0e6' }}>
          <div className="card-body">
            <h5 className="card-title">Month-wise Working Days:</h5>
            <div className="table-responsive">
              <table className="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th style={{ backgroundColor: '#cfe2ff', color: '#052c65' }}>Month</th>
                    <th style={{ backgroundColor: '#cfe2ff', color: '#052c65' }}>Total Days</th>
                    <th style={{ backgroundColor: '#cfe2ff', color: '#052c65' }}>Weekends</th>
                    <th style={{ backgroundColor: '#cfe2ff', color: '#052c65' }}>Holidays</th>
                    <th style={{ backgroundColor: '#cfe2ff', color: '#052c65' }}>Working Days</th>
                  </tr>
                </thead>
                <tbody>
                  {monthWiseData.map((data, index) => (
                    // Alternating row colors
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#e0f2ff' : '#f0faff' }}>
                      <td style={{ color: '#004085' }}>{data.month}</td>
                      <td style={{ color: '#004085' }}>{data.totalDays}</td>
                      <td style={{ color: '#004085' }}>{data.weekendsCount}</td>
                      <td style={{ color: '#004085' }}>{data.holidaysCount}</td>
                      <td style={{ color: '#004085' }}>{data.workingDays}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ORIGINAL UI: Term-wise Working Days table */}
      {termWiseDataOldTable.length > 0 && workingDaysNew !== "N/A" && (
        <div className="card shadow mt-3" style={{ backgroundColor: '#e6ffe6', borderColor: '#90ee90' }}>
          <div className="card-body">
            <h5 className="card-title">Term-wise Working Days:</h5>
            <div className="table-responsive">
              <table className="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th style={{ backgroundColor: '#d4edda', color: '#155724' }}>Term</th>
                    <th style={{ backgroundColor: '#d4edda', color: '#155724' }}>Start Date</th>
                    <th style={{ backgroundColor: '#d4edda', color: '#155724' }}>End Date</th>
                    <th style={{ backgroundColor: '#d4edda', color: '#155724' }}>Total Days</th>
                    <th style={{ backgroundColor: '#d4edda', color: '#155724' }}>Weekends</th>
                    <th style={{ backgroundColor: '#d4edda', color: '#155724' }}>Holidays</th>
                    <th style={{ backgroundColor: '#d4edda', color: '#155724' }}>Working Days</th>
                  </tr>
                </thead>
                <tbody>
                  {termWiseDataOldTable.map((data, index) => (
                    // Alternating row colors
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#e9ffe9' : '#f0fff0' }}>
                      <td style={{ color: '#006600' }}>{data.term}</td>
                      <td style={{ color: '#006600' }}>{data.startDate}</td>
                      <td style={{ color: '#006600' }}>{data.endDate}</td>
                      <td style={{ color: '#006600' }}>{data.totalDays}</td>
                      <td style={{ color: '#006600' }}>{data.weekendsCount}</td>
                      <td style={{ color: '#006600' }}>{data.holidaysCount}</td>
                      <td style={{ color: '#006600' }}>{data.workingDays}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ORIGINAL UI: Detailed Holiday List (re-enabled) */}
      {holidayList.length > 0 && (
        <div className="card mt-3" style={{ backgroundColor: '#fffacd', borderColor: '#ffd700' }}>
          <div className="card-body">
            <h5 className="card-title">Detailed Holiday List (Within Session):</h5>
            <ul className="list-group list-group-flush">
              <ul className="list-group list-group-flush">
                {holidayList.map((holiday, index) => (
                  <li key={index} className="list-group-item" style={{ backgroundColor: 'transparent', color: '#856404' }}> {/* Keep list item background transparent, add text color */}
                    {moment(holiday.date, "DD-MM-YYYY").format("DD MMMM")} - {holiday.name}
                  </li>
                ))}
              </ul>
            </ul>
          </div>
        </div>
      )}

      {/* N/A Alert */}
      {workingDaysNew === "N/A" && (
        <div className="alert alert-warning mt-3" role="alert">
          Please set the Academic Session Start and End Dates to calculate total working days. You can do this from the "Add Session Dates" option in the Academic Calendar menu.
        </div>
      )}
    </div>
  );
};

export default TotalWorkingDaysReport;